/**
 * Admin Reservations Service
 * Handles all reservation-related operations for the admin dashboard
 */

import { supabase } from './supabase';
import {
  addReservationToCalendar,
  updateReservationInCalendar,
  deleteReservationFromCalendar,
} from './googleCalendar';

// =====================================================
// Types
// =====================================================

export interface Reservation {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  reservation_date: string;
  start_time: string;
  end_time: string;
  party_size: number;
  notes: string | null;
  status: 'pending' | 'confirmed' | 'declined' | 'cancelled';
  reservation_type: 'back_room' | 'entire_shop';
  created_at: string;
  approved_by: string | null;
  approved_at: string | null;
  declined_reason: string | null;
  admin_notes: string | null;
  google_calendar_event_id: string | null;
}

export interface ReservationSummary extends Reservation {
  user_first_name: string | null;
  user_last_name: string | null;
  user_email: string | null;
  approved_by_email: string | null;
}

export interface TimeSlot {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
  max_capacity: number;
  slot_type: 'backroom' | 'full_shop';
  created_at: string;
  updated_at: string;
}

export interface AdminNotification {
  id: string;
  notification_type: string;
  reservation_id: string | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
  admin_id: string | null;
  reservation?: Reservation;
}

export interface ReservationFilters {
  status?: 'pending' | 'confirmed' | 'declined' | 'cancelled' | 'all';
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface ReservationStats {
  total: number;
  pending: number;
  confirmed: number;
  declined: number;
  cancelled: number;
  upcomingThisWeek: number;
  upcomingThisMonth: number;
}

// =====================================================
// Reservation Management
// =====================================================

/**
 * Get all reservations with optional filtering
 */
export const getReservations = async (
  filters?: ReservationFilters
): Promise<ReservationSummary[]> => {
  try {
    let query = supabase.from('admin_reservation_summary').select('*');

    // Apply filters
    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters?.dateFrom) {
      query = query.gte('reservation_date', filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte('reservation_date', filters.dateTo);
    }

    if (filters?.search) {
      query = query.or(
        `first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query.order('reservation_date', {
      ascending: true,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching reservations:', error);
    throw error;
  }
};

/**
 * Get reservation by ID
 */
export const getReservationById = async (
  id: string
): Promise<ReservationSummary | null> => {
  try {
    const { data, error } = await supabase
      .from('admin_reservation_summary')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching reservation:', error);
    throw error;
  }
};

/**
 * Get reservations for a specific date
 */
export const getReservationsByDate = async (
  date: string
): Promise<ReservationSummary[]> => {
  try {
    const { data, error } = await supabase
      .from('admin_reservation_summary')
      .select('*')
      .eq('reservation_date', date)
      .in('status', ['confirmed', 'pending'])
      .order('start_time');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching reservations by date:', error);
    throw error;
  }
};

/**
 * Get reservation statistics
 */
export const getReservationStats = async (): Promise<ReservationStats> => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0];
    const monthFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0];

    // Get all reservations
    const { data: allReservations, error: allError } = await supabase
      .from('reservations')
      .select('id, status, reservation_date');

    if (allError) throw allError;

    const reservations = allReservations || [];

    // Calculate stats
    const stats: ReservationStats = {
      total: reservations.length,
      pending: reservations.filter((r) => r.status === 'pending').length,
      confirmed: reservations.filter((r) => r.status === 'confirmed').length,
      declined: reservations.filter((r) => r.status === 'declined').length,
      cancelled: reservations.filter((r) => r.status === 'cancelled').length,
      upcomingThisWeek: reservations.filter(
        (r) =>
          r.status === 'confirmed' &&
          r.reservation_date >= today &&
          r.reservation_date <= weekFromNow
      ).length,
      upcomingThisMonth: reservations.filter(
        (r) =>
          r.status === 'confirmed' &&
          r.reservation_date >= today &&
          r.reservation_date <= monthFromNow
      ).length,
    };

    return stats;
  } catch (error) {
    console.error('Error fetching reservation stats:', error);
    throw error;
  }
};

/**
 * Create a new reservation manually (admin)
 */
export const createReservation = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  reservationDate: string;
  startTime: string;
  endTime: string;
  partySize: number;
  reservationType: 'back_room' | 'entire_shop';
  notes?: string;
}): Promise<Reservation> => {
  try {
    const {
      data: { user: adminUser },
    } = await supabase.auth.getUser();
    if (!adminUser) throw new Error('Not authenticated');

    // Check if a user with this email already exists in profiles
    const { data: existingUser } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', data.email)
      .maybeSingle();

    let guestUserId: string | null;

    if (existingUser) {
      // User exists, use their ID - reservation will show in their account
      guestUserId = existingUser.id;
      console.log('Found existing user for email:', data.email);
    } else {
      // User doesn't exist - use NULL for walk-in guests
      // The reservation table stores all guest contact info (name, email, phone)
      // This prevents the reservation from showing in the admin's account
      guestUserId = null;
      console.log('No account found for email:', data.email, '- creating guest reservation');
    }

    // Add to Google Calendar first (since reservation is auto-confirmed)
    let calendarEventId: string | null = null;
    try {
      calendarEventId = await addReservationToCalendar({
        id: '', // Will be set after creation
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone || null,
        reservation_date: data.reservationDate,
        start_time: data.startTime,
        end_time: data.endTime,
        party_size: data.partySize,
        notes: data.notes || null,
        reservation_type: data.reservationType,
      });
      console.log('[Create Reservation] Added to Google Calendar:', calendarEventId);
    } catch (calendarError) {
      console.error('[Create Reservation] Failed to add to Google Calendar:', calendarError);
      // Don't throw - we still want to create the reservation even if calendar fails
    }

    // Create the reservation with confirmed status (admin-created)
    const { data: reservation, error } = await supabase
      .from('reservations')
      .insert({
        user_id: guestUserId, // Use guest's user ID (existing or newly created)
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone || null,
        reservation_date: data.reservationDate,
        start_time: data.startTime,
        end_time: data.endTime,
        party_size: data.partySize,
        reservation_type: data.reservationType,
        notes: data.notes || null,
        status: 'confirmed', // Auto-confirmed since admin is creating it
        approved_by: adminUser.id,
        approved_at: new Date().toISOString(),
        google_calendar_event_id: calendarEventId,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating reservation:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
      });
      console.error('Reservation data attempted:', {
        user_id: guestUserId,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone || null,
        reservation_date: data.reservationDate,
        start_time: data.startTime,
        end_time: data.endTime,
        party_size: data.partySize,
        reservation_type: data.reservationType,
        notes: data.notes || null,
      });

      // If reservation creation failed but calendar event was created, try to clean it up
      if (calendarEventId) {
        try {
          await deleteReservationFromCalendar(calendarEventId);
          console.log('[Create Reservation] Cleaned up calendar event after failed reservation');
        } catch (cleanupError) {
          console.error('[Create Reservation] Failed to cleanup calendar event:', cleanupError);
        }
      }

      throw error;
    }
    if (!reservation) throw new Error('Failed to create reservation');

    // Send confirmation email via Netlify function
    const functionUrl = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL
      || 'https://ejs-website-redesign.netlify.app/.netlify/functions/send-reservation-approval';

    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          reservationDate: data.reservationDate,
          startTime: data.startTime,
          endTime: data.endTime,
          partySize: data.partySize,
        }),
      });

      if (!response.ok) {
        console.error('Failed to send confirmation email:', await response.text());
      } else {
        console.log('Confirmation email sent successfully');
      }
    } catch (emailError) {
      console.error('Error sending confirmation email:', emailError);
      // Don't throw - reservation was created successfully
    }

    return reservation as Reservation;
  } catch (error) {
    console.error('Error creating reservation:', error);
    throw error;
  }
};

/**
 * Approve a reservation
 */
export const approveReservation = async (
  reservationId: string,
  adminNotes?: string
): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // First, get the reservation details for the email
    const { data: reservation, error: fetchError } = await supabase
      .from('reservations')
      .select('*')
      .eq('id', reservationId)
      .single();

    if (fetchError) throw fetchError;
    if (!reservation) throw new Error('Reservation not found');

    // Add to Google Calendar
    let calendarEventId: string | null = null;
    try {
      calendarEventId = await addReservationToCalendar({
        id: reservation.id,
        first_name: reservation.first_name,
        last_name: reservation.last_name,
        email: reservation.email,
        phone: reservation.phone,
        reservation_date: reservation.reservation_date,
        start_time: reservation.start_time,
        end_time: reservation.end_time,
        party_size: reservation.party_size,
        notes: reservation.notes,
        reservation_type: reservation.reservation_type,
      });
      console.log('[Reservation Approval] Added to Google Calendar:', calendarEventId);
    } catch (calendarError) {
      console.error('[Reservation Approval] Failed to add to Google Calendar:', calendarError);
      // Don't throw - we still want to approve the reservation even if calendar fails
    }

    // Update the reservation status
    const { error: updateError } = await supabase
      .from('reservations')
      .update({
        status: 'confirmed',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        admin_notes: adminNotes || null,
        google_calendar_event_id: calendarEventId,
      })
      .eq('id', reservationId);

    if (updateError) throw updateError;

    // Send approval email via Netlify function
    // Use environment variable if set, otherwise use production Netlify URL
    const functionUrl = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL
      || 'https://ejs-website-redesign.netlify.app/.netlify/functions/send-reservation-approval';

    console.log('[Reservation Approval] Sending email to function URL:', functionUrl);
    console.log('[Reservation Approval] Email data:', {
      email: reservation.email,
      firstName: reservation.first_name,
      lastName: reservation.last_name,
    });

    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: reservation.email,
          firstName: reservation.first_name,
          lastName: reservation.last_name,
          reservationDate: reservation.reservation_date,
          startTime: reservation.start_time,
          endTime: reservation.end_time,
          partySize: reservation.party_size,
          notes: reservation.notes,
        }),
      });

      console.log('[Reservation Approval] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[Reservation Approval] Failed to send email:', errorData);
        // Don't throw - we still want the approval to succeed even if email fails
      } else {
        const successData = await response.json();
        console.log('[Reservation Approval] Email sent successfully:', successData);
      }
    } catch (emailError) {
      console.error('[Reservation Approval] Error sending email:', emailError);
      // Don't throw - we still want the approval to succeed even if email fails
    }
  } catch (error) {
    console.error('Error approving reservation:', error);
    throw error;
  }
};

/**
 * Decline a reservation
 */
export const declineReservation = async (
  reservationId: string,
  reason: string,
  adminNotes?: string
): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Fetch reservation to check for calendar event
    const { data: reservation } = await supabase
      .from('reservations')
      .select('google_calendar_event_id')
      .eq('id', reservationId)
      .single();

    // Remove from Google Calendar if event exists
    if (reservation?.google_calendar_event_id) {
      try {
        await deleteReservationFromCalendar(reservation.google_calendar_event_id);
        console.log('[Reservation Decline] Removed from Google Calendar');
      } catch (calendarError) {
        console.error('[Reservation Decline] Failed to remove from Google Calendar:', calendarError);
        // Don't throw - we still want to decline the reservation even if calendar fails
      }
    }

    const { error } = await supabase
      .from('reservations')
      .update({
        status: 'declined',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        declined_reason: reason,
        admin_notes: adminNotes || null,
        google_calendar_event_id: null, // Clear calendar event ID
      })
      .eq('id', reservationId);

    if (error) throw error;
  } catch (error) {
    console.error('Error declining reservation:', error);
    throw error;
  }
};

/**
 * Cancel a reservation (admin override)
 */
export const cancelReservation = async (
  reservationId: string,
  reason?: string
): Promise<void> => {
  try {
    // First, fetch the reservation details before updating
    const { data: reservation, error: fetchError } = await supabase
      .from('reservations')
      .select('*')
      .eq('id', reservationId)
      .single();

    if (fetchError) throw fetchError;
    if (!reservation) throw new Error('Reservation not found');

    // Remove from Google Calendar if event exists
    if (reservation.google_calendar_event_id) {
      try {
        await deleteReservationFromCalendar(reservation.google_calendar_event_id);
        console.log('[Reservation Cancellation] Removed from Google Calendar');
      } catch (calendarError) {
        console.error('[Reservation Cancellation] Failed to remove from Google Calendar:', calendarError);
        // Don't throw - we still want to cancel the reservation even if calendar fails
      }
    }

    // Update reservation status
    const { error: updateError } = await supabase
      .from('reservations')
      .update({
        status: 'cancelled',
        admin_notes: reason || null,
        google_calendar_event_id: null, // Clear calendar event ID
      })
      .eq('id', reservationId);

    if (updateError) throw updateError;

    // Send cancellation email via Netlify function
    const functionUrl = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL
      || 'https://ejs-website-redesign.netlify.app/.netlify/functions/send-reservation-cancellation';

    console.log('[Reservation Cancellation] Sending email to function URL:', functionUrl);
    console.log('[Reservation Cancellation] Email data:', {
      email: reservation.email,
      firstName: reservation.first_name,
      lastName: reservation.last_name,
    });

    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: reservation.email,
          firstName: reservation.first_name,
          lastName: reservation.last_name,
          reservationDate: reservation.reservation_date,
          startTime: reservation.start_time,
          endTime: reservation.end_time,
          partySize: reservation.party_size,
          cancellationReason: reason,
        }),
      });

      console.log('[Reservation Cancellation] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[Reservation Cancellation] Failed to send email:', errorData);
        // Don't throw - we still want the cancellation to succeed even if email fails
      } else {
        const successData = await response.json();
        console.log('[Reservation Cancellation] Email sent successfully:', successData);
      }
    } catch (emailError) {
      console.error('[Reservation Cancellation] Error sending email:', emailError);
      // Don't throw - we still want the cancellation to succeed even if email fails
    }
  } catch (error) {
    console.error('Error cancelling reservation:', error);
    throw error;
  }
};

/**
 * Update reservation admin notes
 */
export const updateReservationNotes = async (
  reservationId: string,
  notes: string
): Promise<void> => {
  try {
    // First, get the current reservation data
    const { data: reservation, error: fetchError } = await supabase
      .from('reservations')
      .select('*')
      .eq('id', reservationId)
      .single();

    if (fetchError) throw fetchError;

    // Update the database
    const { error } = await supabase
      .from('reservations')
      .update({
        admin_notes: notes,
      })
      .eq('id', reservationId);

    if (error) throw error;

    // Update Google Calendar if event exists and reservation is confirmed
    if (reservation.google_calendar_event_id && reservation.status === 'confirmed') {
      try {
        await updateReservationInCalendar(reservation.google_calendar_event_id, {
          id: reservation.id,
          first_name: reservation.first_name,
          last_name: reservation.last_name,
          email: reservation.email,
          phone: reservation.phone,
          reservation_date: reservation.reservation_date,
          start_time: reservation.start_time,
          end_time: reservation.end_time,
          party_size: reservation.party_size,
          notes: reservation.notes,
          admin_notes: notes, // Use the new notes
          reservation_type: reservation.reservation_type,
        });
        console.log('[Update Notes] Google Calendar event updated successfully');
      } catch (calendarError) {
        console.error('[Update Notes] Failed to update Google Calendar:', calendarError);
        // Don't throw - we still want the notes update to succeed even if calendar fails
      }
    }
  } catch (error) {
    console.error('Error updating reservation notes:', error);
    throw error;
  }
};

/**
 * Edit a reservation (update details, sync calendar, send notification email)
 */
export const editReservation = async (
  reservationId: string,
  updates: {
    reservation_date: string;
    start_time: string;
    end_time: string;
    party_size: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    notes?: string;
  }
): Promise<void> => {
  try {
    // Get original reservation for comparison and calendar update
    const { data: original, error: fetchError } = await supabase
      .from('reservations')
      .select('*')
      .eq('id', reservationId)
      .single();

    if (fetchError) throw fetchError;
    if (!original) throw new Error('Reservation not found');

    // Build list of changes for email
    const changesList: string[] = [];
    if (updates.reservation_date !== original.reservation_date) {
      changesList.push(`Date changed`);
    }
    if (updates.start_time !== original.start_time || updates.end_time !== original.end_time) {
      changesList.push(`Time changed`);
    }
    if (updates.party_size !== original.party_size) {
      changesList.push(`Party size changed from ${original.party_size} to ${updates.party_size}`);
    }
    if (updates.first_name !== original.first_name || updates.last_name !== original.last_name) {
      changesList.push(`Name updated`);
    }

    // Update the database
    const { error: updateError } = await supabase
      .from('reservations')
      .update({
        reservation_date: updates.reservation_date,
        start_time: updates.start_time,
        end_time: updates.end_time,
        party_size: updates.party_size,
        first_name: updates.first_name,
        last_name: updates.last_name,
        email: updates.email,
        phone: updates.phone || null,
        notes: updates.notes || null,
      })
      .eq('id', reservationId);

    if (updateError) throw updateError;

    // Update Google Calendar if event exists and reservation is confirmed
    if (original.google_calendar_event_id && original.status === 'confirmed') {
      try {
        await updateReservationInCalendar(original.google_calendar_event_id, {
          id: original.id,
          first_name: updates.first_name,
          last_name: updates.last_name,
          email: updates.email,
          phone: updates.phone || null,
          reservation_date: updates.reservation_date,
          start_time: updates.start_time,
          end_time: updates.end_time,
          party_size: updates.party_size,
          notes: updates.notes || null,
          admin_notes: original.admin_notes,
          reservation_type: original.reservation_type,
        });
        console.log('[Edit Reservation] Google Calendar event updated');
      } catch (calendarError) {
        console.error('[Edit Reservation] Failed to update Google Calendar:', calendarError);
      }
    }

    // Send update notification email
    const functionUrl = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL
      || 'https://ejs-website-redesign.netlify.app/.netlify/functions';

    try {
      const response = await fetch(`${functionUrl}/send-reservation-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: updates.email,
          firstName: updates.first_name,
          lastName: updates.last_name,
          reservationDate: updates.reservation_date,
          startTime: updates.start_time,
          endTime: updates.end_time,
          partySize: updates.party_size,
          notes: updates.notes,
          changes: changesList.length > 0 ? changesList.join(', ') : 'Reservation details updated',
        }),
      });

      if (!response.ok) {
        console.error('[Edit Reservation] Failed to send update email:', await response.text());
      } else {
        console.log('[Edit Reservation] Update email sent successfully');
      }
    } catch (emailError) {
      console.error('[Edit Reservation] Error sending update email:', emailError);
    }
  } catch (error) {
    console.error('Error editing reservation:', error);
    throw error;
  }
};

/**
 * Bulk approve reservations
 */
export const bulkApproveReservations = async (
  reservationIds: string[]
): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('reservations')
      .update({
        status: 'confirmed',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
      })
      .in('id', reservationIds);

    if (error) throw error;
  } catch (error) {
    console.error('Error bulk approving reservations:', error);
    throw error;
  }
};

/**
 * Bulk decline reservations
 */
export const bulkDeclineReservations = async (
  reservationIds: string[],
  reason: string
): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('reservations')
      .update({
        status: 'declined',
        approved_by: user.id,
        approved_at: new Date().toISOString(),
        declined_reason: reason,
      })
      .in('id', reservationIds);

    if (error) throw error;
  } catch (error) {
    console.error('Error bulk declining reservations:', error);
    throw error;
  }
};

// =====================================================
// Time Slot Management
// =====================================================

/**
 * Get all time slots
 */
export const getTimeSlots = async (): Promise<TimeSlot[]> => {
  try {
    const { data, error } = await supabase
      .from('time_slots')
      .select('*')
      .order('day_of_week')
      .order('start_time');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching time slots:', error);
    throw error;
  }
};

/**
 * Get time slots for a specific day
 */
export const getTimeSlotsForDay = async (
  dayOfWeek: number
): Promise<TimeSlot[]> => {
  try {
    const { data, error } = await supabase
      .from('time_slots')
      .select('*')
      .eq('day_of_week', dayOfWeek)
      .order('start_time');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching time slots for day:', error);
    throw error;
  }
};

/**
 * Create a time slot
 */
export const createTimeSlot = async (
  timeSlot: Omit<TimeSlot, 'id' | 'created_at' | 'updated_at'>
): Promise<TimeSlot> => {
  try {
    const { data, error } = await supabase
      .from('time_slots')
      .insert(timeSlot)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating time slot:', error);
    throw error;
  }
};

/**
 * Update a time slot
 */
export const updateTimeSlot = async (
  id: string,
  updates: Partial<Omit<TimeSlot, 'id' | 'created_at' | 'updated_at'>>
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('time_slots')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating time slot:', error);
    throw error;
  }
};

/**
 * Delete a time slot
 */
export const deleteTimeSlot = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.from('time_slots').delete().eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting time slot:', error);
    throw error;
  }
};

// =====================================================
// Admin Notifications
// =====================================================

/**
 * Get all admin notifications
 */
export const getAdminNotifications = async (
  includeRead = false
): Promise<AdminNotification[]> => {
  try {
    let query = supabase
      .from('admin_notifications')
      .select(
        `
        *,
        reservation:reservation_id (
          id,
          first_name,
          last_name,
          email,
          reservation_date,
          start_time,
          party_size,
          status
        )
      `
      )
      .order('created_at', { ascending: false });

    if (!includeRead) {
      query = query.eq('is_read', false);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching admin notifications:', error);
    throw error;
  }
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (id: string): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('admin_notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
        admin_id: user.id,
      })
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async (): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('admin_notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
        admin_id: user.id,
      })
      .eq('is_read', false);

    if (error) throw error;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 */
export const getUnreadNotificationCount = async (): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('admin_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching unread notification count:', error);
    return 0;
  }
};

// =====================================================
// Conflict Detection
// =====================================================

/**
 * Check for reservation conflicts
 */
export const checkConflicts = async (
  date: string,
  startTime: string,
  endTime: string,
  excludeId?: string
): Promise<{ hasConflict: boolean; conflicts: any[] }> => {
  try {
    const { data, error } = await supabase.rpc('check_reservation_conflicts', {
      p_reservation_date: date,
      p_start_time: startTime,
      p_end_time: endTime,
      p_exclude_id: excludeId || null,
    });

    if (error) throw error;

    const result = data?.[0];
    return {
      hasConflict: (result?.conflict_count || 0) > 0,
      conflicts: result?.conflicting_reservations || [],
    };
  } catch (error) {
    console.error('Error checking conflicts:', error);
    return { hasConflict: false, conflicts: [] };
  }
};
